import {
  ErrorMessage,
  InfoPopup,
  Spinner,
  SuccessPopup,
} from "@/common/components";
import {
  useEffect,
  useContext,
  useState,
  createContext,
  useSelector,
} from "@/common/hooks";
import { PrivateContext } from "@/routes/PrivateRoute";
import {
  useGetAccountBalanceQuery,
  useUpdateProductDetailsMutation,
} from "@/service/ProductService";
import { validate } from "@/utils/Validations";
import { useParams } from "react-router";
import ProductPageOne from "./Common/ProductPageOne";
import ProductPageThree from "./Common/ProductPageThree";
import ProductPageTwo from "./Common/ProductPageTwo";
import { scrollToTop } from "@/utils/Utilities";
import { setProductDetails } from "@/store/productSlice";
import { Constants } from "@/utils/Constants";

export const ProductContext = createContext();

export default function ViewProductDetails() {
  const {
    labels,
    setPageHeader,
    setNavHeader,
    location,
    navigate,
    Routes,
    debounceErrors,
    debounceState,
    accessorPrivileges,
    isRiccAdministrator,
    cloneDeep,
    setNavCancelFunc,
    dispatch,
  } = useContext(PrivateContext);

  const params = useParams();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState(false);
  const [productData, setProductData] = useState({});
  const [cachedProductData] = useState(location?.state);
  const [closeDateCheckbox, setCloseDateCheckbox] = useState(false);
  const [reopenDateCheckbox, setReopenDateCheckbox] = useState(false);
  const [disabledActiveDate, setDisabledActiveDate] = useState(false);
  const [closeAtCheckboxDisabled, setCloseAtCheckboxDisabled] = useState(false);

  const productDataFromStore = useSelector(
    (state) => state?.productsReducer?.productDetails
  );

  const { data: accountBalance } = useGetAccountBalanceQuery({
    productSponsorId: location?.state?.productSponsorId,
    productId: location?.state?.productId,
  });

  const [updateProductApi, { isLoading, error }] =
    useUpdateProductDetailsMutation();

  const readOnlyAccess = !(
    accessorPrivileges["PRODUCT"] === "ADD" ||
    accessorPrivileges["PRODUCT"] === "UPDATE" ||
    isRiccAdministrator
  );

  const onChange = async (value, state) => {
    debounceState({ ...productData, [state]: value }, setProductData);
    debounceErrors(
      { ...errors, [state]: await validate(value, [state]) },
      setErrors
    );
  };

  const errorProps = (state) => {
    return {
      value: productData?.[state],
      error: errors?.[state],
      touched: touched?.[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const readOnlyModeForPaymentTableAgeRange = () => {
    if (productData?.setupComplete === "true") {
      if (isRiccAdministrator) {
        return readOnlyAccess;
      } else {
        return true;
      }
    } else {
      return readOnlyAccess;
    }
  };

  const determineIfProductCanBeClosed = () => {
    if (
      !readOnlyAccess &&
      productData?.activeDt != "" &&
      productData?.setupCompleteDt != "" &&
      productData?.status === "active"
    ) {
      setCloseAtCheckboxDisabled(
        accountBalance?.hasAccountsWithBalance === "true"
      );
    } else {
      setCloseAtCheckboxDisabled(true);
    }
  };

  const backToProductList = () => {
    dispatch(setProductDetails({}));
    navigate(Routes.productList.path);
    return;
  };

  const updateProductDetails = (flag) => {
    if (
      !flag &&
      productData?.setupComplete === "true" &&
      (cachedProductData["PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE"] !==
        productData["PRODUCT_MIN_PAYMENT_TABLE_AGE_RANGE"] ||
        cachedProductData["PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE"] !==
          productData["PRODUCT_MAX_PAYMENT_TABLE_AGE_RANGE"])
    ) {
      setPopupMessage(true);
    } else {
      scrollToTop();
      let payload = cloneDeep(productData);
      let defaultProductOptionSettings = cloneDeep(
        Constants.defaultProductData.productOptionSettings
      );

      const existingSettingsMap = new Map(
        (payload.productOptionSettings || []).map((opt) => [opt.type, opt])
      );

      defaultProductOptionSettings.forEach((opt) => {
        const type = opt.type;
        const value = productData[type];

        if (value === undefined) return;

        if (existingSettingsMap.has(type)) {
          existingSettingsMap.get(type).value = value;
        } else {
          const newSetting = cloneDeep(opt);
          newSetting.value = value;
          newSetting.productSponsorId = productData.productSponsorId;
          payload.productOptionSettings.push(newSetting);
        }
      });
      if (!closeDateCheckbox) {
        payload.closedDt = "";
      }
      if (!reopenDateCheckbox) {
        payload.reopenDt = "";
      }

      updateProductApi(payload)
        .unwrap()
        .then((res) => {
          if (!res.inError) {
            setShowPopup(true);
          }
        });
    }
  };

  useEffect(() => {
    if (productData["PRODUCT_BENEFIT_BASE_RESET"] !== "B") {
      productData["BENEFIT_BASE_RESET_BASED_ON_BIRTHDAY"] = "S";
      setProductData({ ...productData });
    }
  }, [productData?.PRODUCT_BENEFIT_BASE_RESET]);

  useEffect(() => {
    if (reopenDateCheckbox) {
      productData["newInvestorCloseDt"] = "";
      productData["newPurchaseCloseDt"] = "";
      setProductData({ ...productData });
    }
  }, [reopenDateCheckbox]);

  useEffect(() => {
    if (accountBalance) {
      determineIfProductCanBeClosed();
      setCloseDateCheckbox(productData?.closedDt ? true : false);
    }
  }, [accountBalance]);

  useEffect(() => {
    setDisabledActiveDate(
      productData?.setupCompleteDt || productData?.closedDt
    );
  }, [productData]);

  useEffect(() => {
    setPageHeader(labels?.viewProductDetails?.title);
    setNavHeader(labels?.navbar?.product);
    setNavCancelFunc(() => backToProductList);
  }, [params]);

  useEffect(() => {
    if (productDataFromStore?.name) {
      setProductData(cloneDeep(productDataFromStore));
    } else if (location?.state) {
      setProductData(location?.state);
    }
  }, []);

  if (productData?.name)
    return (
      <ProductContext.Provider
        value={{
          labels,
          onChange,
          errorProps,
          productData,
          setProductData,
          readOnlyAccess,
          isRiccAdministrator,
          readOnlyModeForPaymentTableAgeRange,
          closeAtCheckboxDisabled,
          back: () => navigate(-1),
          errors,
          closeDateCheckbox,
          setCloseDateCheckbox,
          reopenDateCheckbox,
          setReopenDateCheckbox,
          disabledActiveDate,
        }}
      >
        {isLoading && <Spinner />}
        <ErrorMessage error={error} />
        {params?.id == 1 ? (
          <ProductPageOne
            next={() => {
              dispatch(setProductDetails(productData));
              navigate(Routes.viewProductDetails.path?.replace(":id", 2), {
                state: productData,
              });
            }}
          />
        ) : params?.id == 2 ? (
          <ProductPageTwo
            next={() => {
              dispatch(setProductDetails(productData));
              navigate(Routes.viewProductDetails.path?.replace(":id", 3), {
                state: productData,
              });
            }}
          />
        ) : params?.id == 3 ? (
          <ProductPageThree submit={updateProductDetails} />
        ) : (
          ""
        )}
        <SuccessPopup
          showModal={showPopup}
          buttonText={labels?.viewProductDetails?.continue}
          click={backToProductList}
          body={labels?.viewProductDetails?.continueMessage}
        />
        <InfoPopup
          showModal={popupMessage}
          primaryButtonText={"OK"}
          primaryButtonClick={() => {
            setPopupMessage(false);
            updateProductDetails(true);
          }}
          body={labels?.viewProductDetails?.paymentTableUpdate}
        />
      </ProductContext.Provider>
    );
}
